import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Send,
  Users,
  User,
  Bell,
  CheckCircle,
  Trash2,
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { PushSubscription, AdminUser } from '../../types';

export const AdminNotifications: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<PushSubscription[]>([]);
  const [stats, setStats] = useState({ total: 0, unique_users: 0 });
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    targetType: 'all' as 'all' | 'specific',
    userId: '',
    title: '',
    body: '',
    url: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [subsRes, usersRes] = await Promise.all([
        adminAPI.getPushSubscriptions(),
        adminAPI.getUsers({ limit: 100 }),
      ]);

      if (subsRes.success) {
        setSubscriptions(subsRes.data.subscriptions);
        setStats(subsRes.data.stats);
      }
      if (usersRes.success) {
        setUsers(usersRes.data.users);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!form.title.trim() || !form.body.trim()) {
      setError('Título y mensaje son requeridos');
      return;
    }

    if (form.targetType === 'specific' && !form.userId) {
      setError('Selecciona un usuario');
      return;
    }

    setIsSending(true);

    try {
      const response = await adminAPI.sendPushNotification({
        userId: form.targetType === 'specific' ? parseInt(form.userId) : undefined,
        title: form.title,
        body: form.body,
        url: form.url || undefined,
      });

      if (response.success) {
        setSuccess(`Notificación enviada a ${response.data.sent} usuario(s)`);
        setForm({ ...form, title: '', body: '', url: '' });
      } else {
        setError('Error al enviar la notificación');
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      setError('Error al enviar la notificación');
    } finally {
      setIsSending(false);
    }
  };

  const handleDeleteSubscription = async (id: number) => {
    if (!window.confirm('¿Eliminar esta suscripción?')) return;

    try {
      const response = await adminAPI.deletePushSubscription(id);
      if (response.success) {
        setSubscriptions(subscriptions.filter(s => s.id !== id));
        setStats(prev => ({ ...prev, total: prev.total - 1 }));
      }
    } catch (error) {
      console.error('Error deleting subscription:', error);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-[var(--color-accent)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[var(--color-text)]">Notificaciones Push</h1>
        <p className="text-[var(--color-text-muted)]">Enviar y gestionar notificaciones push</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-5"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <Bell size={24} className="text-purple-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[var(--color-text)]">{stats.total}</p>
              <p className="text-sm text-[var(--color-text-muted)]">Suscripciones activas</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card p-5"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Users size={24} className="text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[var(--color-text)]">{stats.unique_users}</p>
              <p className="text-sm text-[var(--color-text-muted)]">Usuarios con notificaciones</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Send Notification Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card p-6"
      >
        <h2 className="text-lg font-semibold text-[var(--color-text)] mb-4 flex items-center gap-2">
          <Send size={20} className="text-[var(--color-accent)]" />
          Enviar Notificación
        </h2>

        <form onSubmit={handleSend} className="space-y-4">
          {/* Target Type */}
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="targetType"
                value="all"
                checked={form.targetType === 'all'}
                onChange={() => setForm({ ...form, targetType: 'all', userId: '' })}
                className="w-4 h-4 text-[var(--color-accent)]"
              />
              <Users size={18} className="text-[var(--color-text-muted)]" />
              <span className="text-[var(--color-text)]">Todos los usuarios</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="targetType"
                value="specific"
                checked={form.targetType === 'specific'}
                onChange={() => setForm({ ...form, targetType: 'specific' })}
                className="w-4 h-4 text-[var(--color-accent)]"
              />
              <User size={18} className="text-[var(--color-text-muted)]" />
              <span className="text-[var(--color-text)]">Usuario específico</span>
            </label>
          </div>

          {/* User Select */}
          {form.targetType === 'specific' && (
            <select
              value={form.userId}
              onChange={(e) => setForm({ ...form, userId: e.target.value })}
              className="input"
            >
              <option value="">Selecciona un usuario</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.email})
                </option>
              ))}
            </select>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
              Título
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="input"
              placeholder="Título de la notificación"
              required
            />
          </div>

          {/* Body */}
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
              Mensaje
            </label>
            <textarea
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              className="input min-h-[100px] resize-none"
              placeholder="Contenido de la notificación"
              required
            />
          </div>

          {/* URL (optional) */}
          <div>
            <label className="block text-sm font-medium text-[var(--color-text-muted)] mb-2">
              URL (opcional)
            </label>
            <input
              type="text"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              className="input"
              placeholder="Ej: /profile"
            />
          </div>

          {/* Messages */}
          {error && (
            <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}
          {success && (
            <div className="p-3 rounded-xl bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 text-sm flex items-center gap-2">
              <CheckCircle size={18} />
              {success}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={isSending}
            className="btn btn-primary w-full"
          >
            {isSending ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Send size={18} />
                Enviar Notificación
              </>
            )}
          </button>
        </form>
      </motion.div>

      {/* Subscriptions List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="card overflow-hidden"
      >
        <div className="p-4 border-b border-[var(--color-border)]">
          <h2 className="font-semibold text-[var(--color-text)]">Suscripciones Activas</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg)]">
                <th className="text-left p-4 text-sm font-medium text-[var(--color-text-muted)]">Usuario</th>
                <th className="text-left p-4 text-sm font-medium text-[var(--color-text-muted)]">Fecha</th>
                <th className="text-right p-4 text-sm font-medium text-[var(--color-text-muted)]">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.length === 0 ? (
                <tr>
                  <td colSpan={3} className="p-8 text-center text-[var(--color-text-muted)]">
                    No hay suscripciones activas
                  </td>
                </tr>
              ) : (
                subscriptions.map((sub) => (
                  <tr key={sub.id} className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg)]">
                    <td className="p-4">
                      <p className="font-medium text-[var(--color-text)]">{sub.user_name}</p>
                      <p className="text-xs text-[var(--color-text-muted)]">{sub.user_email}</p>
                    </td>
                    <td className="p-4 text-[var(--color-text-muted)] text-sm">
                      {formatDate(sub.created_at)}
                    </td>
                    <td className="p-4 text-right">
                      <button
                        onClick={() => handleDeleteSubscription(sub.id)}
                        className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
};
